// Shared form for both "New Material" and "Edit Material" flows.
//
// Mode is implicit: when `editingMaterial` is provided, the form prefills its
// fields and submits via `onSave` against an update payload (the parent wires
// the update endpoint). When absent, it submits a create payload. The "Use my
// own code" checkbox is only meaningful in create mode; in edit mode the code
// field is always shown editable so admins can rename the auto-assigned code
// if they want. Non-admin operators see the code read-only in edit mode.
//
// Dev-sync notes carried by this slice:
//   - cbf4c002 — category became a dropdown sourced from the default
//     catalog + categories already in use, with an inline "+ Add new
//     category" path so operators stop typing slightly-different names
//     for the same thing.
//   - 618b2eb5 — vendor field added, persisted into `metadata.vendor`
//     until the alias-table integration ships (see
//     docs/planning/mfg_material_vendor.md).
import { useEffect, useMemo, useState } from "react";
import type { MfgMaterial } from "@/lib/manufacturing/types";
import { buildMaterialCategoryOptions } from "@/lib/manufacturing/materialCategoryCatalog";
import {
  buildMaterialUnitOptions,
  buildShippingUnitOptions,
} from "@/lib/manufacturing/materialUnitCatalog";
import { ManufacturingDatalistInput } from "@/components/manufacturing/ManufacturingDatalistInput";
import { MaterialModalMsdsSection } from "@/components/manufacturing/MaterialModalMsdsSection";
import { MaterialModalShippingFields } from "@/components/manufacturing/MaterialModalShippingFields";
import { MaterialModalVendorField } from "@/components/manufacturing/MaterialModalVendorField";
import { MaterialAllowZeroCostField } from "@/components/manufacturing/MaterialAllowZeroCostField";
import { MaterialReorderThresholdField } from "@/components/manufacturing/MaterialReorderThresholdField";
import { materialAllowsZeroCost } from "@/lib/manufacturing/allowZeroCost";
import { inputClass, mfgPrimaryBtnLgClass, mfgSecondaryBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { parseMfgMsdsStatus, type MfgMsdsStatus } from "@/lib/manufacturing/msdsStatus";
import {
  parseReorderThresholdInput,
  reorderThresholdFromMetadata,
} from "@/lib/manufacturing/reorderThreshold";

const USE_CUSTOM_CODE_STORAGE_KEY = "mfg.materialCode.useCustom";
const ADD_NEW_CATEGORY_SENTINEL = "__add_new__";

export type MaterialFormPayload = {
  /** Undefined when create-mode + auto-code; defined otherwise. */
  code?: string;
  name: string;
  unit: string;
  category: string;
  shipping_unit: string;
  shipping_unit_weight: number;
  /** Pieces in one shipping_unit; defaults to 1 when shipping_unit == unit. */
  pieces_per_shipping_unit: number;
  /**
   * Free-text primary vendor. Always emitted (possibly empty) so update
   * mode can deliberately clear a vendor that's no longer accurate.
   * When `primary_alias_id` is set, server prefers the alias pointer.
   */
  vendor: string;
  /** VEN-2b: existing alias pick (wins over vendor string on save). */
  primary_alias_id?: string | null;
  /** MSDS-1: SDS requirement triage. */
  msds_status: MfgMsdsStatus;
  /** Qty that triggers Fill from below-reorder. Null clears. */
  reorder_threshold: number | null;
  /** True when a $0 unit cost is intentional. */
  allow_zero_cost: boolean;
};

function readUseCustomCode(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(USE_CUSTOM_CODE_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeUseCustomCode(value: boolean): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(USE_CUSTOM_CODE_STORAGE_KEY, value ? "1" : "0");
  } catch {
    // no-op
  }
}

export function MaterialModalForm({
  editingMaterial,
  existingCategories = [],
  existingUnits = [],
  existingShippingUnits = [],
  canEditCode = false,
  onSave,
  onCancel,
}: {
  editingMaterial?: MfgMaterial;
  /**
   * Categories already present on this org's materials. Unioned with the
   * default catalog to populate the category dropdown so operators can
   * pick an existing label rather than re-typing free-text variants.
   */
  existingCategories?: readonly string[];
  /** Units already in use on this org's materials (unioned with defaults). */
  existingUnits?: readonly string[];
  /** Shipping units already in use (unioned with defaults). */
  existingShippingUnits?: readonly string[];
  /** Org admin/owner may rename material codes in edit mode. */
  canEditCode?: boolean;
  onSave: (payload: MaterialFormPayload) => Promise<void>;
  onCancel: () => void;
}) {
  const isEdit = !!editingMaterial;
  const [useCustomCode, setUseCustomCode] = useState<boolean>(false);
  const [code, setCode] = useState(editingMaterial?.code ?? "");
  const [name, setName] = useState(editingMaterial?.name ?? "");
  const [unit, setUnit] = useState(editingMaterial?.unit ?? "each");
  const initialCategory = (editingMaterial?.category ?? "general").trim() || "general";
  const [category, setCategory] = useState(initialCategory);
  // When the operator picks "+ Add new category" we flip to a free-text
  // input until they save (or cancel). Edit-mode that opens against a
  // category not in the catalog starts in "addingCategory" so they don't
  // see a misleading "Add new" picker selection — the actual value just
  // shows in a text input the same way old free-form behaved.
  const [addingCategory, setAddingCategory] = useState(false);
  const [shippingUnit, setShippingUnit] = useState(editingMaterial?.shipping_unit ?? "each");
  const [shippingWeight, setShippingWeight] = useState<string>(
    editingMaterial ? String(editingMaterial.shipping_unit_weight ?? 0) : "0"
  );
  const [piecesPerShippingUnit, setPiecesPerShippingUnit] = useState<string>(
    editingMaterial
      ? String(
          Number(editingMaterial.pieces_per_shipping_unit) > 0
            ? editingMaterial.pieces_per_shipping_unit
            : 1
        )
      : "1"
  );
  // VEN-2b: prefer resolved vendor / primary alias; metadata.vendor is fallback.
  const initialVendor =
    (editingMaterial?.vendor ?? "").trim() ||
    (editingMaterial && typeof editingMaterial.metadata?.vendor === "string"
      ? (editingMaterial.metadata.vendor as string)
      : "");
  const [vendor, setVendor] = useState(initialVendor);
  const [primaryAliasId, setPrimaryAliasId] = useState<string>(
    editingMaterial?.primary_alias_id ?? ""
  );
  const [msdsStatus, setMsdsStatus] = useState<MfgMsdsStatus>(
    parseMfgMsdsStatus(editingMaterial?.msds_status)
  );
  const initialReorder = reorderThresholdFromMetadata(editingMaterial?.metadata);
  const [reorder, setReorder] = useState(initialReorder == null ? "" : String(initialReorder));
  const [allowZeroCost, setAllowZeroCost] = useState(materialAllowsZeroCost(editingMaterial?.metadata));
  const [saving, setSaving] = useState(false);
  const [fieldError, setFieldError] = useState("");

  const categoryOptions = useMemo(
    () => buildMaterialCategoryOptions(existingCategories),
    [existingCategories]
  );
  const unitOptions = useMemo(() => buildMaterialUnitOptions(existingUnits), [existingUnits]);
  const shippingUnitOptions = useMemo(
    () => buildShippingUnitOptions(existingShippingUnits),
    [existingShippingUnits]
  );

  // If we're editing a material whose existing category isn't a recognized
  // option, start the form in "adding new" mode so the user sees the actual
  // value as editable text rather than a select with a phantom default.
  useEffect(() => {
    if (!isEdit) return;
    if (!categoryOptions.includes(initialCategory)) setAddingCategory(true);
  }, [isEdit, categoryOptions, initialCategory]);

  // Only consult localStorage in create mode; edit mode always shows the
  // code field so admins can adjust auto-assigned codes.
  useEffect(() => {
    if (!isEdit) setUseCustomCode(readUseCustomCode());
  }, [isEdit]);

  function toggleUseCustomCode(next: boolean) {
    setUseCustomCode(next);
    writeUseCustomCode(next);
  }

  async function handleSave() {
    if (!isEdit && useCustomCode && !code.trim()) {
      setFieldError("Material code is required when 'Use my own code' is checked");
      return;
    }
    if (isEdit && canEditCode && !code.trim()) {
      setFieldError("Material code cannot be blank");
      return;
    }
    if (!name.trim()) {
      setFieldError("Material name is required");
      return;
    }
    const swParsed = parseFloat(shippingWeight);
    if (!Number.isFinite(swParsed) || swParsed < 0) {
      setFieldError("Shipping unit weight must be a non-negative number");
      return;
    }
    const unitTrim = unit.trim() || "each";
    const shipTrim = shippingUnit.trim();
    let piecesParsed = parseFloat(piecesPerShippingUnit);
    if (!Number.isFinite(piecesParsed) || piecesParsed <= 0) {
      setFieldError("Pieces per shipping unit must be a positive number");
      return;
    }
    if (!shipTrim || shipTrim.toLowerCase() === unitTrim.toLowerCase()) {
      piecesParsed = 1;
    }
    const reorderParsed = parseReorderThresholdInput(reorder);
    if (!reorderParsed.ok) {
      setFieldError(reorderParsed.error);
      return;
    }
    setFieldError("");
    setSaving(true);
    await onSave({
      code:
        isEdit && !canEditCode
          ? undefined
          : isEdit || useCustomCode
            ? code.trim()
            : undefined,
      name: name.trim(),
      unit: unitTrim,
      category: category.trim() || "general",
      shipping_unit: shipTrim,
      shipping_unit_weight: swParsed,
      pieces_per_shipping_unit: piecesParsed,
      vendor: vendor.trim(),
      primary_alias_id: primaryAliasId.trim() ? primaryAliasId.trim() : null,
      msds_status: msdsStatus,
      reorder_threshold: reorderParsed.value,
      allow_zero_cost: allowZeroCost,
    });
    setSaving(false);
  }

  function handleCategoryChange(value: string) {
    if (value === ADD_NEW_CATEGORY_SENTINEL) {
      // Keep the previous category as a starting value so the operator can
      // edit it rather than retyping from scratch. Most "add new" picks
      // are slight variants of an existing label.
      setAddingCategory(true);
      return;
    }
    setCategory(value);
    setAddingCategory(false);
  }

  return (
    <div className="space-y-3">
      {fieldError ? <div className="text-xs text-red-600 dark:text-red-400">{fieldError}</div> : null}

      {!isEdit ? (
        <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-zinc-200">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            checked={useCustomCode}
            onChange={(e) => toggleUseCustomCode(e.target.checked)}
          />
          Use my own code
        </label>
      ) : null}

      {isEdit && !canEditCode ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700 dark:border-zinc-700 dark:bg-zinc-800/40 dark:text-zinc-300">
          Material code: <code className="font-medium">{editingMaterial?.code}</code>
          <span className="mt-1 block text-[11px] text-gray-500 dark:text-zinc-400">
            Code changes require an org admin or owner.
          </span>
        </div>
      ) : isEdit || useCustomCode ? (
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          Material code *
          <input
            className={`${inputClass} mt-1`}
            placeholder="e.g. STL-1-5/8"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        </label>
      ) : (
        <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600 dark:border-zinc-700 dark:bg-zinc-800/40 dark:text-zinc-400">
          Code will be assigned automatically (e.g. <code>FLO-000045</code>) using the category prefix.
        </div>
      )}

      <label className="block text-xs text-gray-600 dark:text-zinc-300">
        Name *
        <input
          className={`${inputClass} mt-1`}
          placeholder="e.g. 1-5/8 Steel Tube"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </label>
      <div className="grid grid-cols-2 gap-2">
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          Unit
          <ManufacturingDatalistInput
            className="mt-1"
            placeholder="each"
            value={unit}
            options={unitOptions}
            onChange={setUnit}
          />
        </label>
        <div className="block text-xs text-gray-600 dark:text-zinc-300">
          <span>Category</span>
          {addingCategory ? (
            <div className="mt-1 flex items-stretch gap-1">
              <input
                className={inputClass}
                placeholder="e.g. siding"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                autoFocus
              />
              <button
                type="button"
                onClick={() => {
                  setCategory(initialCategory);
                  setAddingCategory(false);
                }}
                className="rounded-lg border border-gray-300 bg-white px-2 text-xs text-gray-700 hover:bg-gray-50 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700"
                title="Cancel adding a new category"
              >
                ↺
              </button>
            </div>
          ) : (
            <select
              className={`${inputClass} mt-1`}
              value={categoryOptions.includes(category) ? category : ADD_NEW_CATEGORY_SENTINEL}
              onChange={(e) => handleCategoryChange(e.target.value)}
            >
              {categoryOptions.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
              <option value={ADD_NEW_CATEGORY_SENTINEL}>+ Add new category…</option>
            </select>
          )}
        </div>
      </div>
      <MaterialModalVendorField
        materialId={editingMaterial?.id}
        vendor={vendor}
        primaryAliasId={primaryAliasId}
        onVendorChange={setVendor}
        onPrimaryAliasIdChange={setPrimaryAliasId}
      />
      <MaterialModalMsdsSection
        name={name}
        category={category}
        unit={unit}
        vendor={vendor}
        msdsStatus={msdsStatus}
        onMsdsStatusChange={setMsdsStatus}
      />
      <MaterialReorderThresholdField value={reorder} onChange={setReorder} disabled={saving} />
      <MaterialAllowZeroCostField checked={allowZeroCost} onChange={setAllowZeroCost} disabled={saving} />
      <MaterialModalShippingFields
        unit={unit}
        shippingUnit={shippingUnit}
        shippingUnitOptions={shippingUnitOptions}
        onShippingUnitChange={setShippingUnit}
        shippingWeight={shippingWeight}
        onShippingWeightChange={setShippingWeight}
        piecesPerShippingUnit={piecesPerShippingUnit}
        onPiecesPerShippingUnitChange={setPiecesPerShippingUnit}
      />
      <div className="flex gap-2 pt-2">
        <button type="button" className={mfgPrimaryBtnLgClass} disabled={saving} onClick={() => void handleSave()}>
          {saving ? (isEdit ? "Updating…" : "Saving…") : isEdit ? "Update" : "Save"}
        </button>
        <button type="button" className={mfgSecondaryBtnClass} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
